/* eslint-disable import/max-dependencies */
import {
  TableActionGroup,
  TableActions,
  SimpleTableActionButton,
  ConfirmTableActionButton,
} from "@fas/ui-core";
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { getTableSelections, type DataItem } from "@fas/ui-framework";
import {
  Edit, Visibility, Delete, Check, FileCopy, GetApp, Archive,
} from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import type { PageTypes } from "../ExportPageFd/types/ExportPageFd.types";
import type { Page, Version } from "./types/PagesList.types";
import type { State } from "../../pages/AppStoreWrapper";
import Can from "../Can";
import { activateDocumentSaga, archivePageSaga } from "../../actions/pages/actions";
import { pagesListBulkDeleteSaga } from "../../actions/pages";
import { getPageVerisionPreviewSaga } from "../../actions/page";
import { openClonePageDialog, openExportPageDialog } from "../../actions/pageActions";
import externalRedirect from "../../helpers/ExternalRedirect";
import { getHashValue } from "../../services/jusCan";
import { PAGES_LIST } from "../../helpers/constants";

type PropsActions = {
  items: Array<Page>,
};

export function PreviewAction({ items }: PropsActions) {
  const dispatch = useDispatch();

  const handlePreview = (documentId: string, versionCollection: Array<Version>) => {
    const currentVersion = versionCollection.find(({ version }) => version === "current");
    if (currentVersion) {
      dispatch(getPageVerisionPreviewSaga(documentId, currentVersion.id));
    }
  };

  return (
    <SimpleTableActionButton
      tooltip="Preview"
      onClick={() => items.map(({ documentId, versionCollection }) => handlePreview(documentId, versionCollection))}
      Icon={<Visibility />}
    />
  );
}

export function EditAction({ items }: PropsActions) {
  const hashId = getHashValue();

  const handleEdit = (documentId: string) => {
    externalRedirect({ to: `/pages/edit/${documentId}?hashId=${hashId}`, target: "_blank" });
  };

  return (
    <SimpleTableActionButton
      tooltip="Edit"
      onClick={() => items.map(({ documentId }) => handleEdit(documentId))}
      Icon={<Edit />}
    />
  );
}

export function EditGeneralAction({ items }: PropsActions) {
  const hashId = getHashValue();

  const handleEditGeneralInfo = (documentId: string) => {
    externalRedirect({ to: `/pages/editInfo/${documentId}?hashId=${hashId}`, target: "_blank" });
  };

  return (
    <SimpleTableActionButton
      tooltip="EditGeneralInfo"
      onClick={() => items.map(({ documentId }) => handleEditGeneralInfo(documentId))}
      Icon={<SettingsIcon />}
    />
  );
}

export function ActivateAction({ items }: PropsActions) {
  const dispatch = useDispatch();
  const handleActivate = (id: string) => dispatch(activateDocumentSaga(id));

  const currentItem = items[0] as Page;

  const isActive: boolean = currentItem.versionCollection.some(
    ({ version }: Version) => version === "current"
  );

  const isHaveNew: boolean = currentItem.versionCollection.some(
    ({ version }: Version) => version === "new"
  );

  const isDisabled = items.length > 1 || isActive || !isHaveNew;

  return (
    <Can permissions={["documentFactory.activateVersion"]}>
      <ConfirmTableActionButton
        tooltip="Activate"
        viewPath="activate"
        items={[currentItem]}
        disabled={isDisabled}
        onClick={() => {
          handleActivate(currentItem.documentId);
        }}
        Icon={<Check />}
        confirmTitle="Activate items "
        confirmText="Are you sure you want to activate "
      />
    </Can>
  );
}

export function DeleteAction({ items }: PropsActions) {
  const dispatch = useDispatch();
  const handleBulkDelete = (ids: Array<string>) => {
    dispatch(pagesListBulkDeleteSaga(ids));
  };

  return (
    <Can permissions={["documentFactory.remove"]}>
      <ConfirmTableActionButton
        tooltip="Delete"
        viewPath="delete"
        items={items.map((i) => ({ id: i.documentId, name: i.name }))}
        onClick={() => handleBulkDelete(items.map((i) => i.documentId))}
        Icon={<Delete />}
        confirmTitle="Delete items "
        confirmText="Are you sure you want to delete "
      />
    </Can>
  );
}

export function CloneAction({ items }: PropsActions) {
  const dispatch = useDispatch();
  const handleClone = (pageId: string, pageName: string, pageType: PageTypes, pageUrl: string) => {
    dispatch(openClonePageDialog(pageId, pageName, pageType, pageUrl));
  };

  const currentItem = items[0] as Page;

  return (
    <SimpleTableActionButton
      tooltip="Clone"
      disabled={items.length > 1}
      onClick={() => {
        const { aliasCollection } = currentItem;
        const last = aliasCollection[aliasCollection.length - 1];
        const url: string = last?.alias || "";
        handleClone(currentItem.documentId, currentItem.name, currentItem.type as PageTypes, url);
      }}
      Icon={<FileCopy />}
    />
  );
}

export function ExportAction({ items }: PropsActions) {
  const dispatch = useDispatch();
  const handleExport = (pageId: string, pageName: string, pageType: PageTypes) => {
    dispatch(openExportPageDialog(pageId, pageName, pageType));
  };

  const currentItem = items[0] as Page;

  const isActive: boolean = currentItem.versionCollection.some(
    ({ version }: Version) => version === "current"
  );

  const isDisabled = items.length > 1 || (!(["page", "pft"].includes(currentItem.type) && isActive));

  return (
    <SimpleTableActionButton
      tooltip="Export"
      disabled={isDisabled}
      onClick={() => {
        handleExport(currentItem.documentId, currentItem.name, currentItem.type as PageTypes);
      }}
      Icon={<GetApp />}
    />
  );
}

export function ArchiveAction({ items }: PropsActions) {
  const dispatch = useDispatch();
  const handleArchive = (id: string) => {
    dispatch(archivePageSaga(id));
  };

  const currentItem = items[0] as Page;

  return (
    <Can permissions={["documentFactory.remove"]}>
      <ConfirmTableActionButton
        tooltip="Archive"
        viewPath="archive"
        items={items.map((i) => ({ id: i.documentId, name: i.name }))}
        disabled={items.length > 1}
        onClick={() => {
          handleArchive(currentItem.documentId);
        }}
        Icon={<Archive />}
        confirmTitle="Archive items "
        confirmText="Are you sure you want to archive "
      />
    </Can>
  );
}

function PagesActions() {
  const items = useSelector(
    (state: State): Array<Page & DataItem> => getTableSelections(state, PAGES_LIST)
  );

  return (
    <TableActions>
      <TableActionGroup>

        <EditAction items={items} />

        <EditGeneralAction items={items} />

        <PreviewAction items={items} />

        <ActivateAction items={items} />

        <DeleteAction items={items} />

        <CloneAction items={items} />

        <ExportAction items={items} />

        <ArchiveAction items={items} />

      </TableActionGroup>
    </TableActions>
  );
}

export default PagesActions;
